// --- React ---
import React, { useState, useEffect, memo } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Spinner, Text, Card, CardTitle, Flex, LabeledSelectBox } from '@/components/ui';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 타입 ---
import type { MonitoringDeviceListProps } from './monitoringDeviceTypes';

// --- 스타일 ---
import { StyledTable, StyledTh } from './MonitoringDeviceList.styles';

// --- 페이지 컴포넌트 ---
import { DeviceTableRow } from './DeviceTableRow';

// ==================== 서브 컴포넌트 ====================

/** 마지막 데이터 갱신으로부터 경과 시간 표시 (1초마다 갱신) */
const TimeAgo: React.FC<{ dataUpdatedAt: number }> = memo(({ dataUpdatedAt }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    setCurrentTime(Date.now());
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  return <>{Math.floor((currentTime - dataUpdatedAt) / 1000)}s ago</>;
});

// ==================== 컴포넌트 ====================

export const MonitoringDeviceList: React.FC<MonitoringDeviceListProps> = ({
  monitoringData,
  isLoading = false,
  isConnectedFilter,
  onConnectionFilterChange,
  onDeviceClick,
  dataUpdatedAt,
  isAdmin,
  onForceDisconnect,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation('monitoring');

  // ==================== 상태 관리 ====================

  const [expandedDevices, setExpandedDevices] = useState<Set<string>>(new Set());

  // ==================== 상수 ====================

  const connectedOptions = [
    { label: t('common:all'), value: '' },
    { label: t('common:connected'), value: 'true' },
    { label: t('common:disconnected'), value: 'false' },
  ];

  // ==================== 핸들러 ====================

  /**
   * 디바이스 행 확장/축소 토글.
   * 확장 시 해당 마커로 지도 이동도 함께 수행.
   */
  const toggleDeviceExpansion = (serialNumber: string) => {
    setExpandedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serialNumber)) {
        newSet.delete(serialNumber);
      } else {
        newSet.add(serialNumber);
      }
      return newSet;
    });

    if (onDeviceClick) {
      onDeviceClick(serialNumber);
    }
  };

  // ==================== 렌더링 ====================

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
        }}
      >
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, height: '100%', minHeight: 0, display: 'flex' }}>
      <Card
        padding={4}
        gap={0}
        radius="lg"
        height="auto"
        layout="flex"
        fullWidth
        className="monitoring-device-list-card"
      >
        {/* 헤더: 타이틀 + 연결 필터 */}
        <Flex direction="row" align="center" justify="between" gap={6} padding={4}>
          <Flex direction="row" align="start" gap={2}>
            <CardTitle>Monitoring Devices</CardTitle>
            <Text size="md" weight="regular" color="secondary">
              ({monitoringData.length} devices -{' '}
              {dataUpdatedAt && <TimeAgo dataUpdatedAt={dataUpdatedAt} />})
            </Text>
          </Flex>
          <LabeledSelectBox
            label={'connection'}
            value={isConnectedFilter === 'all' ? '' : String(isConnectedFilter)}
            options={connectedOptions}
            onChange={option => {
              const value = option.value as string;
              onConnectionFilterChange(value === '' ? 'all' : value === 'true');
            }}
            width="120px"
            isEdit={true}
            align="right"
            size="sm"
            labelSize="md"
            labelWeight="semibold"
          />
        </Flex>

        {/* 디바이스 테이블 */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {monitoringData.length === 0 ? (
            <div
              style={{
                padding: theme.spacing[7],
                textAlign: 'center',
                color: theme.colors.text.secondary,
              }}
            >
              <Text>{t(i18nKeys.monitoring.no_devices_found)}</Text>
            </div>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <StyledTh style={{ width: '100px' }}>Device</StyledTh>
                  <StyledTh style={{ width: '100px' }}>Site</StyledTh>
                  <StyledTh style={{ width: '100px' }}>Location</StyledTh>
                  <StyledTh style={{ width: '100px' }}>Worker</StyledTh>
                  <StyledTh style={{ width: '40px' }}>App</StyledTh>
                  <StyledTh style={{ width: '40px' }}>GPS</StyledTh>
                  <StyledTh style={{ width: '100px' }}>Status</StyledTh>
                  <StyledTh style={{ width: '80px' }}>Temp(°C)</StyledTh>
                  <StyledTh style={{ width: '70px' }}>Battery</StyledTh>
                  <StyledTh style={{ width: '90px' }}>Cal Due</StyledTh>
                  <StyledTh style={{ width: '90px' }}>Bump Due</StyledTh>
                  <StyledTh style={{ width: '150px' }}>Last Updated</StyledTh>
                </tr>
              </thead>
              <tbody>
                {monitoringData.map(item => (
                  <DeviceTableRow
                    key={item.device.serial_number}
                    item={item}
                    isExpanded={expandedDevices.has(item.device.serial_number)}
                    onToggle={toggleDeviceExpansion}
                    isAdmin={isAdmin}
                    onForceDisconnect={onForceDisconnect}
                  />
                ))}
              </tbody>
            </StyledTable>
          )}
        </div>
      </Card>
    </div>
  );
};
